// React hooks over utils/pricing.js. Both expose a `status` that screens use
// to (a) show a loading note, (b) show an error banner with Retry, and (c)
// refuse to save when the price is not known. Neither ever falls back to a
// hardcoded rate.
import { useState, useEffect, useCallback } from 'react';
import { fetchRates, fetchPricingLookup } from '../utils/pricing';

// Rates for ONE cruise + deck. Re-reads when either changes or on retry().
//   status: 'idle'    — cruise or deck not chosen yet (nothing to read)
//           'loading' — read in flight; rates is null
//           'ok'      — rates = { adult, child }
//           'error'   — query failed, row missing, or unusable price; rates null
export function usePricingRates(cruise, deck) {
  const [rates, setRates]   = useState(null);
  const [status, setStatus] = useState(cruise && deck ? 'loading' : 'idle');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!cruise || !deck) { setRates(null); setStatus('idle'); return undefined; }
    let cancelled = false;
    setRates(null);
    setStatus('loading');
    fetchRates(cruise, deck).then(({ rates: r, error }) => {
      if (cancelled) return;
      if (error) console.error('[pricing] rates read failed:', error.message || error);
      if (r) { setRates(r); setStatus('ok'); }
      else { setRates(null); setStatus('error'); }
    });
    return () => { cancelled = true; };
  }, [cruise, deck, attempt]);

  const retry = useCallback(() => setAttempt(n => n + 1), []);
  return { rates, status, retry };
}

// The whole pricing table as { [cruise]: { [deck]: { adult, child } } }, for
// screens that price many combinations. Pair with ratesFor(lookup, cruise,
// deck) from utils/pricing.js to resolve one slot (null = no usable price).
//   status: 'loading' — read in flight; lookup is null
//           'ok'      — lookup populated (a given slot may still be missing)
//           'error'   — query failed or table empty; lookup null
export function usePricingLookup() {
  const [lookup, setLookup] = useState(null);
  const [status, setStatus] = useState('loading');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    fetchPricingLookup().then(({ lookup: lk, error }) => {
      if (cancelled) return;
      if (error) console.error('[pricing] lookup read failed:', error.message || error);
      if (lk) { setLookup(lk); setStatus('ok'); }
      else { setLookup(null); setStatus('error'); }
    });
    return () => { cancelled = true; };
  }, [attempt]);

  const retry = useCallback(() => setAttempt(n => n + 1), []);
  return { lookup, status, retry };
}
